import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { AuthScrollOnFocusDirective } from '@features/auth/presentation/utils/auth-scroll-on-focus.directive';
import { AuthService } from '@features/auth/services/auth.service';
import {
  authErrorI18nKey,
  resolveAuthRequestErrorKind,
} from '@features/auth/utils/auth-request-error.util';

/** Mismo patrón que el login: local@dominio.tld (sin espacios). */
const EMAIL_FORMAT_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Component({
  selector: 'app-forgot-password',
  imports: [
    RouterModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    AuthShowcaseAsideComponent,
    AuthScrollOnFocusDirective,
  ],
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent {
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  isSubmitting = false;
  errorMessage = '';
  validationAttempted = false;

  form = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_FORMAT_PATTERN),
    ]),
  });

  get f() {
    return this.form.controls;
  }

  submit(): void {
    this.validationAttempted = true;
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    const email = (this.form.value.email ?? '').trim();
    this.form.patchValue({ email }, { emitEvent: false });
    this.errorMessage = '';
    this.isSubmitting = true;

    this.authService
      .requestPasswordReset(email)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          void this.router.navigate(['/authentication/reset-password'], {
            queryParams: { email },
          });
        },
        error: (err: unknown) => {
          const kind = resolveAuthRequestErrorKind(err);
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.forgotPassword', kind));
        },
      });
  }
}
